import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-email.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-add.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-checkmark.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-close.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';

import '../../components/confirmation-dialog';
import type { EmailAccount } from '../../api/email-accounts.js';
import { emailAccountService } from '../../services/email-account-service.js';
import { settingsSharedStyles } from './settings-shared.styles';

@customElement('kv-settings-email')
@localized()
export class KvSettingsEmail extends LitElement {
  static styles = [
    settingsSharedStyles,
    css`
      .account-card {
        min-height: 168px;
      }

      .account-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
        gap: 12px;
      }

      .account-info {
        align-items: flex-start;
        display: flex;
        flex: 1;
        gap: 10px;
        min-width: 0;
      }

      .account-copy {
        min-width: 0;
      }

      .account-name {
        color: var(--color-app-text);
        font-size: 14px;
        font-weight: 700;
        line-height: 1.25;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .account-email {
        color: var(--color-app-text-secondary);
        font-size: 12px;
        line-height: 1.35;
        margin-top: 3px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .provider-icon {
        width: 30px;
        height: 30px;
      border-radius: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
        flex: 0 0 auto;
      }

      .provider-gmail {
      background: #ea4335;
      color: white;
      }

      .provider-outlook {
      background: #0078d4;
      color: white;
      }

      .provider-generic {
      background: #6c757d;
      color: white;
      }

      .account-actions {
        display: flex;
        gap: 8px;
        margin-top: 14px;
      }

      .add-account-card {
        width: 100%;
      }
    `,
  ];

  @state()
  accessor emailAccounts: EmailAccount[] = [];

  @state()
  accessor emailLoading = true;

  @state()
  accessor emailError: string | null = null;

  @state()
  accessor pendingAccountDelete: EmailAccount | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.loadEmailAccounts();
  }

  private async loadEmailAccounts() {
    this.emailLoading = true;
    this.emailError = null;
    try {
      this.emailAccounts = await emailAccountService.getAccounts();
    } catch (err) {
      this.emailError = msg('Failed to load email accounts');
      console.error('Error loading email accounts:', err);
    } finally {
      this.emailLoading = false;
    }
  }

  private openEmailAccountSetup(loginHint?: string) {
    const authUrl = new URL(
      '/api/v1/email-accounts/oauth2/setup',
      window.location.origin
    );
    if (loginHint) {
      authUrl.searchParams.set('login_hint', loginHint);
    }

    window.open(authUrl, '_blank', 'width=600,height=700');

    setTimeout(() => {
      this.loadEmailAccounts();
    }, 3000);
  }

  private addEmailAccount() {
    this.openEmailAccountSetup();
  }

  private reconnectEmailAccount(account: EmailAccount) {
    this.openEmailAccountSetup(account.email);
  }

  private requestDeleteAccount(account: EmailAccount) {
    this.pendingAccountDelete = account;
  }

  private cancelDeleteAccount = () => {
    this.pendingAccountDelete = null;
  };

  private confirmDeleteAccount = async () => {
    const account = this.pendingAccountDelete;
    if (!account) return;
    this.pendingAccountDelete = null;
    try {
      await emailAccountService.deleteAccount(account.id);
      await this.loadEmailAccounts();
    } catch (err) {
      this.emailError = msg('Failed to delete email account');
      console.error('Error deleting email account:', err);
    }
  };

  private getProviderIcon(provider: string): string {
    switch (provider) {
      case 'gmail':
        return 'G';
      case 'outlook':
        return 'O';
      case 'generic':
        return 'M';
      default:
        return '?';
    }
  }

  private getProviderClass(provider: string): string {
    return `provider-${provider}`;
  }

  private getAccountDisplayName(account: EmailAccount): string {
    const emailParts = account.email.split('@');
    const username = emailParts[0];

    return username
      .replace(/[._]/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  private renderEmailAccount(account: EmailAccount) {
    return html`
      <div class="settings-card account-card">
        <div class="account-header">
          <div class="account-info">
            <span class="provider-icon ${this.getProviderClass(account.provider)}">
              ${this.getProviderIcon(account.provider)}
            </span>
            <div class="account-copy">
              <div class="account-name">${this.getAccountDisplayName(account)}</div>
              <div class="account-email">${account.email}</div>
            </div>
          </div>
          <div class="settings-chip success">
            <sp-icon-checkmark size="s"></sp-icon-checkmark>
            ${msg('Active')}
          </div>
        </div>
        
        <div class="settings-detail-list">
          <div class="settings-detail-row">
            <span class="settings-detail-label">${msg('Provider')}</span>
            <span class="settings-detail-value">${account.provider}</span>
          </div>
          <div class="settings-detail-row">
            <span class="settings-detail-label">${msg('Account ID')}</span>
            <span class="settings-detail-value">${account.id}</span>
          </div>
        </div>

        <div class="account-actions">
          ${
            account.provider === 'outlook'
              ? html`
                <sp-button
                  size="s"
                  variant="secondary"
                  @click=${() => this.reconnectEmailAccount(account)}
                >
                  ${msg('Reconnect')}
                </sp-button>
              `
              : ''
          }
          <sp-button 
            size="s" 
            variant="secondary"
            class="settings-danger-button"
            @click=${() => this.requestDeleteAccount(account)}
          >
            <sp-icon-delete slot="icon"></sp-icon-delete>
            ${msg('Delete')}
          </sp-button>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <section class="settings-section">
        <header class="settings-header">
          <div class="settings-title-row">
            <span class="settings-title-icon">
              <sp-icon-email size="s"></sp-icon-email>
            </span>
            <h2 class="settings-title">${msg('Email Accounts')}</h2>
          </div>
          <p class="settings-description">
            ${msg('Manage OAuth2 email accounts for workflow email-read and email-send tasks.')}
          </p>
        </header>

        ${
          this.emailLoading
            ? html`
              <div class="settings-loading">
                <sp-progress-circle
                  label=${msg('Loading email accounts')}
                  indeterminate
                ></sp-progress-circle>
                ${msg('Loading email accounts...')}
              </div>
            `
            : this.emailError
              ? html`
                <div class="settings-message error">
                  <sp-icon-close size="s"></sp-icon-close>
                  ${this.emailError}
                </div>
              `
              : html`
                <div class="settings-card-grid">
                  ${repeat(
                    this.emailAccounts,
                    (account) => account.id,
                    (account) => this.renderEmailAccount(account)
                  )}
                  
                  <button
                    type="button"
                    class="settings-action-card add-account-card"
                    @click=${this.addEmailAccount}
                  >
                    <div class="settings-action-content">
                      <span class="settings-action-icon">
                        <sp-icon-add size="s"></sp-icon-add>
                      </span>
                      <div>
                        <div class="settings-action-title">${msg('Add Email Account')}</div>
                        <div class="settings-action-description">
                          ${msg('Connect a new account using OAuth2 authentication.')}
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              `
        }
        ${
          this.pendingAccountDelete
            ? html`
              <kv-confirmation-dialog
                heading=${msg('Delete email account')}
                confirm-label=${msg('Delete')}
                @cancel=${this.cancelDeleteAccount}
                @confirm=${this.confirmDeleteAccount}
              >
                <p>
                  ${msg(
                    str`Delete email account "${this.pendingAccountDelete.email}"? Workflow email tasks using this OAuth account will stop working until another account is configured.`
                  )}
                </p>
              </kv-confirmation-dialog>
            `
            : ''
        }
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-settings-email': KvSettingsEmail;
  }
}
